import React, { Component } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Pagination,
  PaginationItem,
  PaginationLink,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import axios from "axios";
import cookie from "react-cookies";
import moment from "moment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import OrderDetails from "../../components/common/OrderDetails";
import constants from "../../Constants"; // Adjust path as necessary
import { QIKTRUCK_CONSUMER_SITE } from "../../constants/mobileApp";
import {
  downloadCustomerInvoice,
  getTrackOrderPayUrl,
} from "../../utils/customerInvoice";
import {
  downloadBillingCSV,
  downloadBillingPDF,
  buildBillingPDFBlob,
} from "../../utils/billingStatement";
import "../integrations/integrations.css";
import "./billing.css";

const BANK_DETAILS = {
  accountName: "QikTruck (Pty) Ltd",
  bank: "First National Bank (FNB)",
  accountNumber: "62934406986",
  branchCode: "255355",
  accountType: "Gold Business Account",
};

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  LineController,
  BarController
);

const baseURL = constants.baseURL;
// Base URL for API calls

// Maximum number of page links to show

// Authorization helper function
const Authorization = (method) => ({
  headers: {
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  },
});

class Billing extends Component {
  constructor(props) {
    super(props);
    this.state = {
      invoices: [],
      weeklyDue: 0,
      monthlyDue: 0,
      accountFee: 0,
      accountFeeVat: 0,
      monthlyTripsDue: 0,
      isLoading: true,
      viewMode: "monthly", // 'monthly' or 'weekly'
      monthlyData: [],
      weeklyData: [],
      limit: 100,
      offset: 0,
      currentPage: 1,
      totalCount: 0,
      hasMoreRecords: false,
      selectedOrder: null,
      startChat: "a",
      orderFiles: [],
      orderConversations: [],
      newComment: "",
      addingConversation: false,
      summary: {
        orders: 0,
        tripsperday: 0,
        quotations: 0,
      },
      paidVsUnpaidData: {
        paid: 0,
        unpaid: 0,
      },
      openMenu: null,
      emailingInvoiceId: null,
      downloadingInvoiceId: null,
      selectedItems: [], // Array to store selected trip items
      isDrawerOpen: false, // Control drawer visibility
      selectAllPeriods: {}, // Per-period select-all state keyed by period name
      billingProfile: {},
      canPayBilling: true,
      accountEmail: "",
      showAnalytics: false,
      statusFilter: "all",
      invoiceSearch: "",
      dateFrom: "",
      dateTo: "",
      copiedField: null,
      emailModalOpen: false,
      emailModalInvoices: [],
      emailModalPeriodLabel: "",
      emailModalAccountFee: null,
      emailExtra1: "",
      emailExtra2: "",
      emailSending: false,
      statementQuota: { used: 0, limit: 5, remaining: 5 },
    };
  }

  // Helper: get invoice effective amount in rands.
  // The /customer-billing-history API returns sellingprice/100 as `amount` (rands).
  // HQ dashboard, in contrast, stores raw `sellingprice` in cents.
  // Prefer API-provided rand field `amount`; if only `sellingprice` is present,
  // assume it is in cents (matching HQ) and convert.
  getInvoiceAmount = (invoice) => {
    if (invoice.amount !== undefined && invoice.amount !== null) {
      return this.parseAmount(invoice.amount);
    }
    if (invoice.sellingprice !== undefined && invoice.sellingprice !== null) {
      return this.parseAmount(invoice.sellingprice) / 100;
    }
    return 0;
  };

  // Mirror HQ dashboard rules (hq/src/pages/dashboard/section.js getOrderCalculations)
  // to derive commission / VAT / driver-pay from the gross amount.
  // Defaults: servicefee 20% VAT-inclusive, VAT 15%.
  getInvoiceBreakdown = (invoice) => {
    const gross = this.getInvoiceAmount(invoice);
    const servicefee =
      typeof invoice?.servicefee === "number" ? invoice.servicefee : 0.2;
    const fee = gross * servicefee; // VAT-inclusive service fee
    const commission = fee / 1.15; // commission ex VAT
    const vat = commission * 0.15;
    const driverpay = Math.max(gross - (commission + vat), 0);
    return { gross, commission, vat, driverpay };
  };

  // Account fee (SaaS) is excl. VAT; HQ invoices add 15% VAT on the fee.
  getAccountFeeVat = (fee = this.state.accountFee) => {
    const af = Number(fee) || 0;
    return af > 0 ? af * 0.15 : 0;
  };

  // Helper: safely parse an amount string while preserving sign (including trailing minus)
  parseAmount = (value) => {
    if (value === null || value === undefined) return 0;
    const str = (value + "").trim();
    const trailingNeg = /-$/.test(str);
    const cleaned = str.replace(/[^0-9.-]+/g, "").replace(/-/g, "");
    const num = parseFloat(cleaned) || 0;
    return trailingNeg ? -num : (/^-/.test(str) ? -num : num);
  };

  // Helper: treat paid flag loosely (driverpaid and PayFast payment status)
  isPaid = (invoice) => {
    const status = String(invoice.status || invoice.paymentstatus || "").toUpperCase();
    return (
      invoice.driverpaid === 1 ||
      invoice.driverpaid === "1" ||
      invoice.driverpaid === true ||
      status === "PAID" ||
      status === "COMPLETE"
    );
  };

  getInvoiceEmail = (invoice) =>
    (invoice.email || this.state.accountEmail || "").trim();

  getTrackOrderUrl = (invoice, { payNow = false } = {}) => {
    const orderid = invoice.orderid || invoice.id;
    const email = this.getInvoiceEmail(invoice);
    if (!orderid) return null;
    const params = new URLSearchParams({ orderid: String(orderid) });
    if (email) params.set("email", email);
    if (payNow) params.set("paynow", "yes");
    return `${QIKTRUCK_CONSUMER_SITE}/track-order?${params.toString()}`;
  };

  componentDidMount() {
    document.title = "Billing | QikTruck";
    this.fetchBillingData();
    this.fetchSummary();
    this.fetchBillingProfile();
    this._onDocClick = (e) => {
      if (
        e.target.closest?.(".qt-row-menu-trigger") ||
        e.target.closest?.(".qt-row-menu-panel--fixed")
      ) {
        return;
      }
      this.closeRowMenu();
    };
    this._onScrollClose = () => this.closeRowMenu();
    document.addEventListener("click", this._onDocClick);
    window.addEventListener("scroll", this._onScrollClose, true);
    window.addEventListener("resize", this._onScrollClose);
  }

  componentWillUnmount() {
    document.removeEventListener("click", this._onDocClick);
    window.removeEventListener("scroll", this._onScrollClose, true);
    window.removeEventListener("resize", this._onScrollClose);
  }

  toggleRowMenu = (invoice, menuContext, e) => {
    e.preventDefault();
    e.stopPropagation();
    const key = String(invoice.id);
    if (String(this.state.openMenu?.invoice?.id) === key) {
      this.closeRowMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 200;
    let left = rect.right - menuWidth;
    if (left < 8) left = 8;
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8;
    }
    let top = rect.bottom + 4;
    const estimatedHeight = 200;
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - 4);
    }
    this.setState({
      openMenu: {
        invoice,
        ...menuContext,
        top,
        left,
      },
    });
  };

  closeRowMenu = () => this.setState({ openMenu: null });

  fetchBillingProfile = () => {
    axios
      .get(`${baseURL}/merchant/account-profile`, Authorization())
      .then((response) => {
        if (response.data?.billing) {
          this.setState({
            billingProfile: response.data.billing,
            accountEmail: response.data.account?.email || "",
          });
        }
      })
      .catch(() => {});
  };

  copyToClipboard = (field, value) => {
    if (!value) return;
    const text = String(value);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        this.setState({ copiedField: field });
        toast.success("Copied to clipboard");
        setTimeout(() => this.setState({ copiedField: null }), 2000);
      });
    }
  };

  getPaymentReference = () => {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return "";
    if (selectedItems.length === 1) return `INV-${selectedItems[0].id}`;
    return `INV-${selectedItems[0].id}-+${selectedItems.length - 1}`;
  };

  getFilteredDisplayData = () => {
    const { viewMode, monthlyData, weeklyData, statusFilter, invoiceSearch, accountFee } =
      this.state;
    const displayData = viewMode === "monthly" ? monthlyData : weeklyData;
    const query = (invoiceSearch || "").trim().toLowerCase();
    const af = Number(accountFee) || 0;
    const afInclusive = af + this.getAccountFeeVat(af);

    return displayData
      .map((period) => {
        let invoices = period.invoices;
        if (statusFilter === "paid") {
          invoices = invoices.filter((i) => this.isPaid(i));
        } else if (statusFilter === "unpaid") {
          invoices = invoices.filter((i) => !this.isPaid(i));
        }
        if (query) {
          invoices = invoices.filter((i) =>
            String(i.id || "").toLowerCase().includes(query)
          );
        }
        const tripsTotal = invoices.reduce(
          (sum, inv) => sum + this.getInvoiceAmount(inv),
          0
        );
        // Monthly periods include account fee + VAT (HQ invoice math); weekly is trips only.
        const totalAmount =
          viewMode === "monthly" ? tripsTotal + afInclusive : tripsTotal;
        return {
          ...period,
          invoices,
          tripsTotal,
          totalAmount,
          accountFee: viewMode === "monthly" ? af : 0,
          accountFeeVat: viewMode === "monthly" ? this.getAccountFeeVat(af) : 0,
        };
      })
      .filter((period) => period.invoices.length > 0);
  };

  getPageUnpaidTotal = () => {
    const { invoices } = this.state;
    return invoices
      .filter((i) => !this.isPaid(i))
      .reduce((sum, i) => sum + this.getInvoiceAmount(i), 0);
  };

  getPageUnpaidCount = () => {
    return this.state.invoices.filter((i) => !this.isPaid(i)).length;
  };

  // Fetch billing history data with pagination
  fetchBillingHistory = () => {
    const { limit, offset, dateFrom, dateTo } = this.state;
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo) params.set("dateTo", dateTo);

    return axios
      .get(`${baseURL}/customer-billing-history?${params.toString()}`, Authorization())
      .then((response) => {
        let invoices = [];
        let totalCount = 0;

        if (Array.isArray(response.data)) {
          invoices = response.data;
          totalCount =
            response.headers["x-total-count"] ||
            response.headers["X-Total-Count"] ||
            invoices.length;
        } else if (response.data && typeof response.data === "object") {
          invoices =
            response.data.invoices ||
            response.data.data ||
            response.data.items ||
            [];
          totalCount =
            response.data.totalCount ||
            response.data.total ||
            response.data.count ||
            response.data.meta?.total ||
            invoices.length;
        }

        const hasMoreRecords = offset + invoices.length < totalCount;
        return { invoices, totalCount, hasMoreRecords };
      });
  };

  applyDateFilter = () => {
    this.setState(
      {
        offset: 0,
        currentPage: 1,
        isLoading: true,
      },
      () => this.fetchBillingData()
    );
  };

  clearDateFilter = () => {
    this.setState(
      {
        dateFrom: "",
        dateTo: "",
        offset: 0,
        currentPage: 1,
        isLoading: true,
      },
      () => this.fetchBillingData()
    );
  };

  handleDateFilterChange = (field, value) => {
    this.setState({ [field]: value });
  };

  fetchSummary = () => {
    axios
      .get(`${baseURL}/customer-stats-summary`, Authorization())
      .then((response) => {
        this.setState({ summary: response.data });
      })
      .catch((error) => {
        console.error("Error fetching billing summary:", error);
        return { summary: { orders: 0, tripsperday: 0, quotations: 0 } };
      });
  };

  // Fetch billing data from API
  fetchBillingData = () => {
    const { limit, offset } = this.state;
    this.setState({ isLoading: true });

    console.log(`Fetching data with limit: ${limit}, offset: ${offset}`);

    // Fetch billing summary data (weekly and monthly totals)
    const fetchSummary = axios
      .get(`${baseURL}/customer-billing-summary`, Authorization())
      .then((response) => ({
        weeklyDue: response.data.weeklyDue || 0,
        monthlyDue: response.data.monthlyDue || 0,
        monthlyTripsDue:
          response.data.monthlyTripsDue ?? response.data.monthlyDue ?? 0,
        accountFee: response.data.accountFee || 0,
        accountFeeVat: response.data.accountFeeVat || 0,
        canPayBilling: response.data.canPayBilling !== false,
      }))
      .catch((error) => {
        console.error("Error fetching billing summary:", error);
        return {
          weeklyDue: 0,
          monthlyDue: 0,
          monthlyTripsDue: 0,
          accountFee: 0,
          accountFeeVat: 0,
          canPayBilling: true,
        };
      });

    // Fetch billing history with pagination
    const fetchHistory = this.fetchBillingHistory().catch((error) => {
      console.error("Error fetching billing history:", error);
      return { invoices: [], totalCount: 0, hasMoreRecords: false };
    });

    // Wait for both requests to complete
    Promise.all([fetchSummary, fetchHistory]).then(
      ([summaryData, historyData]) => {
        const { invoices, totalCount, hasMoreRecords } = historyData;

        // Calculate paid vs unpaid for chart (note: reflects current page only)
        const paidCount = invoices.filter((invoice) => this.isPaid(invoice))
          .length;
        const unpaidCount = invoices.length - paidCount;

        this.setState({
          weeklyDue: summaryData.weeklyDue,
          monthlyDue: summaryData.monthlyDue,
          monthlyTripsDue: summaryData.monthlyTripsDue,
          accountFee: summaryData.accountFee,
          accountFeeVat: summaryData.accountFeeVat,
          canPayBilling: summaryData.canPayBilling !== false,
          invoices,
          totalCount,
          hasMoreRecords,
          monthlyData: this.groupByMonth(invoices, summaryData.accountFee),
          weeklyData: this.groupByWeek(invoices),
          paidVsUnpaidData: {
            paid: paidCount,
            unpaid: unpaidCount,
          },
          isLoading: false,
        });
      }
    );
  };

  // Handle page change
  handlePageChange = (pageNumber) => {
    const { limit } = this.state;
    const newOffset = (pageNumber - 1) * limit;

    this.setState(
      {
        currentPage: pageNumber,
        offset: newOffset,
        isLoading: true,
      },
      () => {
        this.fetchBillingData();
      }
    );
  };

  // Load next or previous batch of records
  loadBatch = (direction) => {
    const { offset, limit, totalCount } = this.state;
    let newOffset;
    if (direction === "next") {
      newOffset = offset + limit;
      // Ensure we don't go past the end
      if (newOffset >= totalCount) {
        newOffset = Math.max(0, totalCount - limit);
      }
    } else {
      // Previous batch
      newOffset = Math.max(0, offset - limit);
    }

    const newPage = Math.floor(newOffset / limit) + 1;

    this.setState(
      {
        offset: newOffset,
        currentPage: newPage,
        isLoading: true,
      },
      () => {
        this.fetchBillingData();
      }
    );
  };

  // Group invoices by month
  groupByMonth = (invoices, accountFee = this.state.accountFee) => {
    if (!invoices || invoices.length === 0) {
      return [];
    }

    const groups = {};
    const af = Number(accountFee) || 0;
    const afInclusive = af + this.getAccountFeeVat(af);

    invoices.forEach((invoice) => {
      if (!invoice.date) {
        console.warn("Invoice missing date:", invoice);
        return;
      }

      const monthYear = moment(invoice.date).format("MMMM YYYY");

      if (!groups[monthYear]) {
        groups[monthYear] = {
          period: monthYear,
          tripsTotal: 0,
          totalAmount: 0,
          invoices: [],
        };
      }

      const amount = this.getInvoiceAmount(invoice);
      groups[monthYear].invoices.push(invoice);
      groups[monthYear].tripsTotal += amount;
      groups[monthYear].totalAmount += amount;
    });

    // Match HQ monthly statements: add account fee + VAT once per month.
    Object.values(groups).forEach((group) => {
      group.totalAmount = group.tripsTotal + afInclusive;
      group.accountFee = af;
      group.accountFeeVat = this.getAccountFeeVat(af);
    });

    // Sort groups by most recent invoice date in group
    return Object.values(groups).sort((a, b) => {
      const aMax = Math.max(...a.invoices.map((i) => moment(i.date).valueOf()));
      const bMax = Math.max(...b.invoices.map((i) => moment(i.date).valueOf()));
      return bMax - aMax;
    });
  };

  // Group invoices by week
  groupByWeek = (invoices) => {
    if (!invoices || invoices.length === 0) {
      return [];
    }

    const groups = {};

    invoices.forEach((invoice) => {
      if (!invoice.date) {
        console.warn("Invoice missing date:", invoice);
        return;
      }

      const date = moment(invoice.date);
      const weekKey = `Week ${date.week()}, starting (${date
        .startOf("week")
        .format("ddd DD MMM YYYY")})`;

      if (!groups[weekKey]) {
        groups[weekKey] = {
          period: weekKey,
          totalAmount: 0,
          invoices: [],
        };
      }

      groups[weekKey].invoices.push(invoice);
      groups[weekKey].totalAmount += this.getInvoiceAmount(invoice);
    });

    // Sort groups by most recent invoice date in group
    return Object.values(groups).sort((a, b) => {
      const aMax = Math.max(...a.invoices.map((i) => moment(i.date).valueOf()));
      const bMax = Math.max(...b.invoices.map((i) => moment(i.date).valueOf()));
      return bMax - aMax;
    });
  };

  // Toggle between monthly and weekly views
  toggleView = (viewMode) => {
    this.setState({ viewMode });
  };

  // Format currency for display (ZAR)
  formatCurrency = (amount) => {
    const value = parseFloat(amount);
    if (!isFinite(value)) return "R 0.00";
    return new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Handle loading previous batch of records
  handlePreviousBatch = (e) => {
    if (e) e.preventDefault();
    this.loadBatch("previous");
  };

  // Handle loading next batch of records
  handleNextBatch = (e) => {
    if (e) e.preventDefault();
    this.loadBatch("next");
  };

  // Get files associated with an order
  getOrderFiles = (orderId) => {
    axios
      .get(`${baseURL}/order-files/${orderId}`, Authorization())
      .then((response) => {
        this.setState({ orderFiles: response.data || [] });
      })
      .catch((error) => {
        console.error("Error fetching order files:", error);
        this.setState({ orderFiles: [] });
      });
  };

  // Get conversations for an order
  getOrderConversations = (orderId) => {
    axios
      .get(`${baseURL}/order-conversations/${orderId}`, Authorization())
      .then((response) => {
        this.setState({ orderConversations: response.data || [] });
      })
      .catch((error) => {
        console.error("Error fetching order conversations:", error);
        this.setState({ orderConversations: [] });
      });
  };

  // Add a conversation to an order
  addConversation = (orderId) => {
    const { newComment } = this.state;

    if (!newComment.trim()) return;

    this.setState({ addingConversation: true });

    axios
      .post(
        `${baseURL}/order-conversation`,
        { orderId, message: newComment },
        Authorization()
      )
      .then(() => {
        this.getOrderConversations(orderId);
        this.setState({ newComment: "", addingConversation: false });
      })
      .catch((error) => {
        console.error("Error adding conversation:", error);
        this.setState({ addingConversation: false });
      });
  };

  // Get image for truck type
  getImage = (truckType) => {
    const truckImages = {
      "1 Ton": "/assets/images/trucks/1ton.png",
      "4 Ton": "/assets/images/trucks/4ton.png",
      "8 Ton": "/assets/images/trucks/8ton.png",
      default: "/assets/images/trucks/default.png",
    };

    return truckImages[truckType] || truckImages.default;
  };

  // Get chart data for monthly billing amounts
  getMonthlyChartData = () => {
    const { monthlyData } = this.state;

    // Limit to last 6 months for better visualization
    const recentMonths = monthlyData.slice(0, 6).reverse();

    return {
      labels: recentMonths.map((data) => data.period),
      datasets: [
        {
          label: "Monthly Billing Amount",
          data: recentMonths.map((data) => data.totalAmount),
          backgroundColor: "rgba(54, 162, 235, 0.5)",
          borderColor: "rgba(54, 162, 235, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Get chart data for weekly billing amounts
  getWeeklyChartData = () => {
    const { weeklyData } = this.state;

    // Limit to last 8 weeks for better visualization
    const recentWeeks = weeklyData.slice(0, 8).reverse();

    return {
      labels: recentWeeks.map((data) =>
        data.period
          .replace("Week ", "W")
          .replace(", starting (", "\n")
          .replace(")", "")
      ),
      datasets: [
        {
          label: "Weekly Billing Amount",
          data: recentWeeks.map((data) => data.totalAmount),
          backgroundColor: "rgba(75, 192, 192, 0.5)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    };
  };

  // Get chart data for paid vs unpaid invoices
  getPaidVsUnpaidData = () => {
    const { paidVsUnpaidData } = this.state;

    return {
      labels: ["Paid", "Unpaid"],
      datasets: [
        {
          data: [paidVsUnpaidData.paid, paidVsUnpaidData.unpaid],
          backgroundColor: [
            "rgba(75, 192, 192, 0.5)",
            "rgba(255, 159, 64, 0.5)",
          ],
          borderColor: ["rgba(75, 192, 192, 1)", "rgba(255, 159, 64, 1)"],
          borderWidth: 1,
        },
      ],
    };
  };

  // Chart options
  getChartOptions = (title) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: title,
        },
      },
    };
  };

  // Get chart data for billing comparison (current vs previous period)
  getBillingComparisonData = () => {
    const { monthlyData, weeklyData, viewMode } = this.state;
    const series = viewMode === "weekly" ? weeklyData : monthlyData;

    if (series.length < 2) {
      return {
        labels: ["Current", "Previous"],
        datasets: [
          {
            label: "Period Comparison",
            data: [0, 0],
            backgroundColor: [
              "rgba(54, 162, 235, 0.5)",
              "rgba(153, 102, 255, 0.5)",
            ],
            borderColor: ["rgba(54, 162, 235, 1)", "rgba(153, 102, 255, 1)"],
            borderWidth: 1,
          },
        ],
      };
    }

    // Get current and previous period amounts
    const currentPeriod = series[0];
    const previousPeriod = series[1];

    return {
      labels: ["Current Period", "Previous Period"],
      datasets: [
        {
          label:
            viewMode === "monthly" ? "Monthly Comparison" : "Weekly Comparison",
          data: [currentPeriod.totalAmount, previousPeriod.totalAmount],
          backgroundColor: [
            "rgba(54, 162, 235, 0.5)",
            "rgba(153, 102, 255, 0.5)",
          ],
          borderColor: ["rgba(54, 162, 235, 1)", "rgba(153, 102, 255, 1)"],
          borderWidth: 1,
        },
      ],
    };
  };

  // Get chart data for payment status timeline
  getPaymentStatusTimelineData = () => {
    const { monthlyData } = this.state;

    // Limit to last 6 months for visualization
    const recentMonths = monthlyData.slice(0, 6).reverse();

    const datasets = [
      {
        label: "Paid",
        data: recentMonths.map((month) =>
          month.invoices
            .filter((invoice) => this.isPaid(invoice))
            .reduce((sum, invoice) => sum + this.getInvoiceAmount(invoice), 0)
        ),
        backgroundColor: "rgba(75, 192, 192, 0.5)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
      {
        label: "Unpaid",
        data: recentMonths.map((month) =>
          month.invoices
            .filter((invoice) => !this.isPaid(invoice))
            .reduce((sum, invoice) => sum + this.getInvoiceAmount(invoice), 0)
        ),
        backgroundColor: "rgba(255, 159, 64, 0.5)",
        borderColor: "rgba(255, 159, 64, 1)",
        borderWidth: 1,
      },
    ];

    return {
      labels: recentMonths.map((data) => data.period),
      datasets,
    };
  };

  // Get chart data for order volume trends
  getOrderVolumeTrendData = () => {
    const { monthlyData } = this.state;

    // Limit to last 6 months for visualization
    const recentMonths = monthlyData.slice(0, 6).reverse();

    return {
      labels: recentMonths.map((data) => data.period),
      datasets: [
        {
          label: "Order Volume",
          data: recentMonths.map((data) => data.invoices.length),
          backgroundColor: "rgba(153, 102, 255, 0.5)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 1,
          type: "bar",
        },
        {
          label: "Average Order Value",
          data: recentMonths.map((data) => {
            const totalAmount = data.totalAmount;
            const count = data.invoices.length;
            return count > 0 ? totalAmount / count : 0;
          }),
          backgroundColor: "rgba(255, 99, 132, 0.5)",
          borderColor: "rgba(255, 99, 132, 1)",
          borderWidth: 2,
          type: "line",
          yAxisID: "y1",
        },
      ],
    };
  };

  // Get mixed chart options
  getMixedChartOptions = (title) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: title,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Number of Orders",
          },
        },
        y1: {
          beginAtZero: true,
          position: "right",
          title: {
            display: true,
            text: "Average Order Value (R)",
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };
  };

  // Get date range for current view
  getDateRange = () => {
    const { viewMode, monthlyData, weeklyData } = this.state;
    const displayData = viewMode === "monthly" ? monthlyData : weeklyData;

    if (!displayData || displayData.length === 0) {
      return "";
    }

    // Get all invoices from current view data
    const allInvoices = displayData.flatMap((period) => period.invoices);

    if (allInvoices.length === 0) {
      return "";
    }

    // Sort invoices by date
    const sortedInvoices = allInvoices.sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()
    );

    const startDate = moment(sortedInvoices[0].date);
    const endDate = moment(sortedInvoices[sortedInvoices.length - 1].date);

    // Format the date range based on view mode
    if (viewMode === "monthly") {
      if (startDate.isSame(endDate, "month")) {
        return startDate.format("MMMM YYYY");
      }
      return `${startDate.format("DD MMM YYYY")} - ${endDate.format(
        "DD MMM YYYY"
      )}`;
    } else {
      // Weekly view
      if (startDate.isSame(endDate, "week")) {
        return `Week of ${startDate.format("DD MMM YYYY")}`;
      }
      return `${startDate.format("DD MMM YYYY")} - ${endDate.format(
        "DD MMM YYYY"
      )}`;
    }
  };

  // Get stack chart options
  getStackedChartOptions = (title) => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: title,
        },
      },
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: "Amount (R)",
          },
        },
      },
    };
  };

  // Email invoice PDF to customer (async - does not block the table)
  emailInvoice = (invoiceId) => {
    const destination = this.state.accountEmail || "your account email";
    this.closeRowMenu();
    this.setState({ emailingInvoiceId: invoiceId });
    const toastId = toast.info(`Generating invoice #${invoiceId}…`, {
      autoClose: false,
      closeButton: false,
    });

    axios
      .post(
        `${baseURL}/customer-invoice-document`,
        { invoiceid: invoiceId },
        Authorization()
      )
      .then(() => {
        toast.update(toastId, {
          render: `Invoice #${invoiceId} sent to ${destination}`,
          type: "success",
          autoClose: 5000,
          closeButton: true,
          isLoading: false,
        });
      })
      .catch((error) => {
        console.error("Error emailing invoice:", error);
        toast.update(toastId, {
          render: `Could not email invoice #${invoiceId}. Try again or contact support.`,
          type: "error",
          autoClose: 6000,
          closeButton: true,
          isLoading: false,
        });
      })
      .finally(() => {
        this.setState({ emailingInvoiceId: null });
      });
  };

  downloadInvoice = (invoiceId) => {
    this.closeRowMenu();
    this.setState({ downloadingInvoiceId: invoiceId });
    downloadCustomerInvoice(invoiceId, cookie.load("session-id-qt")).finally(() => {
      this.setState({ downloadingInvoiceId: null });
    });
  };

  openOrderDetails = (invoice, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const orderid = invoice.orderid || invoice.id;
    const email = this.getInvoiceEmail(invoice);
    if (!orderid) return;

    axios
      .post(`${baseURL}/track-order`, { orderid, email }, Authorization())
      .then((response) => {
        if (response.data?.orderid) {
          this.setState({
            selectedOrder: response.data,
            startChat: "ax-x-x" + response.data.orderid,
          });
          this.getOrderFiles(orderid);
          this.getOrderConversations(orderid);
        } else {
          toast.error("Could not open trip details.");
        }
      })
      .catch((error) => {
        console.error("Error opening trip details:", error);
        toast.error("Could not open trip details. Try again or contact support.");
      });
  };

  renderInvoiceRowMenu = (invoice, menuContext) => {
    const key = String(invoice.id);
    const isOpen = String(this.state.openMenu?.invoice?.id) === key;
    const isEmailing = String(this.state.emailingInvoiceId) === key;
    const isDownloading = String(this.state.downloadingInvoiceId) === key;
    const isBusy = isEmailing || isDownloading;

    return (
      <button
        type="button"
        className={"qt-row-menu-trigger" + (isOpen ? " is-active" : "")}
        aria-label={`Actions for trip ${invoice.id}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={(e) => this.toggleRowMenu(invoice, menuContext, e)}
        disabled={isBusy}
      >
        {isBusy ? (
          <Spinner size="sm" />
        ) : (
          <i className="mdi mdi-dots-vertical" />
        )}
      </button>
    );
  };

  renderFloatingRowMenu = () => {
    const { openMenu } = this.state;
    if (!openMenu) return null;
    const { invoice, trackUrl, payUrl, paid, top, left } = openMenu;

    return (
      <div
        className="qt-row-menu-panel qt-row-menu-panel--fixed"
        role="menu"
        style={{ top, left }}
      >
        {trackUrl ? (
          <a
            href={trackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="qt-row-menu-item"
            role="menuitem"
            onClick={this.closeRowMenu}
          >
            <i className="mdi mdi-map-marker-path" />
            View trip
          </a>
        ) : null}
        {payUrl ? (
          <a
            href={payUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="qt-row-menu-item qt-row-menu-item--pay"
            role="menuitem"
            onClick={this.closeRowMenu}
          >
            <i className="mdi mdi-credit-card-outline" />
            Pay with PayFast
          </a>
        ) : null}
        <button
          type="button"
          className="qt-row-menu-item"
          role="menuitem"
          onClick={() => this.emailInvoice(invoice.id)}
        >
          <i className="mdi mdi-email-outline" />
          Email invoice PDF
        </button>
        <button
          type="button"
          className="qt-row-menu-item"
          role="menuitem"
          onClick={() => this.downloadInvoice(invoice.id)}
        >
          <i className="mdi mdi-download" />
          Download invoice PDF
        </button>
        {paid ? (
          <span className="qt-row-menu-hint">Trip marked paid</span>
        ) : (
          <span className="qt-row-menu-hint">Or pay via EFT in the sidebar</span>
        )}
      </div>
    );
  };

  // Toggle item selection
  toggleItemSelection = (invoice) => {
    const { selectedItems } = this.state;
    const isSelected = selectedItems.some((item) => item.id === invoice.id);

    if (isSelected) {
      // Remove item from selection
      this.setState({
        selectedItems: selectedItems.filter((item) => item.id !== invoice.id),
      });
    } else {
      // Add item to selection
      this.setState({
        selectedItems: [...selectedItems, invoice],
      });
    }
  };

  // Toggle select all items within a specific period
  togglePeriodSelectAll = (period) => {
    const { selectedItems, selectAllPeriods } = this.state;
    const periodIds = new Set(period.invoices.map((i) => i.id));
    const isAllSelected = period.invoices.every((i) =>
      selectedItems.some((s) => s.id === i.id)
    );

    if (isAllSelected) {
      this.setState({
        selectedItems: selectedItems.filter((i) => !periodIds.has(i.id)),
        selectAllPeriods: { ...selectAllPeriods, [period.period]: false },
      });
    } else {
      const existingIds = new Set(selectedItems.map((i) => i.id));
      const toAdd = period.invoices.filter((i) => !existingIds.has(i.id));
      this.setState({
        selectedItems: [...selectedItems, ...toAdd],
        selectAllPeriods: { ...selectAllPeriods, [period.period]: true },
      });
    }
  };

  // Toggle drawer visibility
  toggleDrawer = () => {
    this.setState((prevState) => ({
      isDrawerOpen: !prevState.isDrawerOpen,
    }));
  };

  // Trips subtotal for the current selection (rands).
  calculateSelectedTripsTotal = () => {
    const { selectedItems } = this.state;
    return selectedItems.reduce(
      (total, item) => total + this.getInvoiceAmount(item),
      0
    );
  };

  // HQ invoice amount for EFT: selected trips + account fee + VAT on fee.
  calculateSelectedTotal = () => {
    const trips = this.calculateSelectedTripsTotal();
    if (!this.state.selectedItems.length) return 0;
    const af = Number(this.state.accountFee) || 0;
    return trips + af + this.getAccountFeeVat(af);
  };

  // Clear all selected items
  clearSelection = () => {
    this.setState({
      selectedItems: [],
      selectAllPeriods: {},
      isDrawerOpen: false,
    });
  };

  removeSelectedItem = (invoiceId) => {
    this.setState((prev) => {
      const selectedItems = prev.selectedItems.filter(
        (item) => item.id !== invoiceId
      );
      return {
        selectedItems,
        isDrawerOpen: selectedItems.length > 0 ? prev.isDrawerOpen : false,
      };
    });
  };

  getStatementMeta = (invoices, periodLabel) => {
    const promocode =
      cookie.load("promocode") ||
      invoices.find((i) => i.promocode)?.promocode ||
      "customer";
    const customerName = cookie.load("ctName") || promocode;
    const email =
      this.state.accountEmail ||
      invoices.find((i) => i.email)?.email ||
      "";
    const endDate = invoices.reduce((max, inv) => {
      const t = moment(inv.date).valueOf();
      return Number.isFinite(t) && t > max ? t : max;
    }, 0);
    return {
      accountFee: Number(this.state.accountFee) || 0,
      periodLabel: periodLabel || moment().format("MMMM YYYY"),
      promocode,
      customerName,
      email,
      endDate: endDate || undefined,
    };
  };

  downloadPeriodCSV = (period) => {
    const invoices = period?.invoices || [];
    if (!invoices.length) {
      toast.info("No trips to download for this period");
      return;
    }
    const meta = this.getStatementMeta(invoices, period.period);
    if (this.state.viewMode !== "monthly") {
      meta.accountFee = 0;
    } else if (period.accountFee != null) {
      meta.accountFee = Number(period.accountFee) || 0;
    }
    const ok = downloadBillingCSV(invoices, meta);
    if (ok) toast.success(`CSV downloaded for ${period.period}`);
  };

  downloadPeriodPDF = (period) => {
    const invoices = period?.invoices || [];
    if (!invoices.length) {
      toast.info("No trips to download for this period");
      return;
    }
    const meta = this.getStatementMeta(invoices, period.period);
    if (this.state.viewMode !== "monthly") {
      meta.accountFee = 0;
    } else if (period.accountFee != null) {
      meta.accountFee = Number(period.accountFee) || 0;
    }
    const ok = downloadBillingPDF(invoices, meta);
    if (ok) toast.success(`PDF invoice downloaded for ${period.period}`);
  };

  downloadSelectedCSV = () => {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return;
    const periodLabel =
      selectedItems.length === 1
        ? moment(selectedItems[0].date).format("MMMM YYYY")
        : `selection-${selectedItems.length}-trips`;
    const ok = downloadBillingCSV(
      selectedItems,
      this.getStatementMeta(selectedItems, periodLabel)
    );
    if (ok) toast.success("CSV downloaded for selected trips");
  };

  downloadSelectedPDF = () => {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return;
    const periodLabel = moment(selectedItems[0].date).format("MMMM YYYY");
    const ok = downloadBillingPDF(
      selectedItems,
      this.getStatementMeta(selectedItems, periodLabel)
    );
    if (ok) toast.success("PDF invoice downloaded for selected trips");
  };

  fetchStatementQuota = () => {
    axios
      .get(`${baseURL}/customer-billing-statement-quota`, Authorization())
      .then((res) => {
        if (res.data) {
          this.setState({
            statementQuota: {
              used: res.data.used || 0,
              limit: res.data.limit || 5,
              remaining:
                res.data.remaining != null
                  ? res.data.remaining
                  : Math.max(0, (res.data.limit || 5) - (res.data.used || 0)),
            },
          });
        }
      })
      .catch(() => {});
  };

  openEmailModal = (invoices, periodLabel, accountFeeOverride) => {
    const list = invoices || [];
    if (!list.length) {
      toast.info("No trips to email");
      return;
    }
    this.fetchStatementQuota();
    let fee = Number(this.state.accountFee) || 0;
    if (accountFeeOverride != null) {
      fee = Number(accountFeeOverride) || 0;
    } else if (this.state.viewMode !== "monthly") {
      fee = 0;
    }
    this.setState({
      emailModalOpen: true,
      emailModalInvoices: list,
      emailModalPeriodLabel:
        periodLabel || moment(list[0].date).format("MMMM YYYY"),
      emailModalAccountFee: fee,
      emailExtra1: "",
      emailExtra2: "",
    });
  };

  closeEmailModal = () => {
    if (this.state.emailSending) return;
    this.setState({
      emailModalOpen: false,
      emailModalInvoices: [],
      emailModalPeriodLabel: "",
      emailModalAccountFee: null,
      emailExtra1: "",
      emailExtra2: "",
    });
  };

  sendStatementEmail = () => {
    const {
      emailModalInvoices,
      emailModalPeriodLabel,
      emailModalAccountFee,
      emailExtra1,
      emailExtra2,
      statementQuota,
      accountFee,
      accountEmail,
    } = this.state;

    if (!emailModalInvoices.length) return;
    if ((statementQuota.remaining || 0) <= 0) {
      toast.error(
        `Daily limit reached (${statementQuota.limit} emails/day). Try again tomorrow.`
      );
      return;
    }

    const extra = [emailExtra1, emailExtra2]
      .map((e) => String(e || "").trim().toLowerCase())
      .filter(Boolean);
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const e of extra) {
      if (!emailRe.test(e)) {
        toast.error(`Invalid email: ${e}`);
        return;
      }
    }

    const meta = this.getStatementMeta(
      emailModalInvoices,
      emailModalPeriodLabel
    );
    if (emailModalAccountFee != null) {
      meta.accountFee = Number(emailModalAccountFee) || 0;
    }
    const formData = new FormData();
    formData.append(
      "orderIds",
      JSON.stringify(emailModalInvoices.map((i) => i.id || i.orderid))
    );
    formData.append("additionalEmails", JSON.stringify(extra));
    formData.append(
      "accountFee",
      String(meta.accountFee ?? accountFee ?? 0)
    );
    formData.append("periodLabel", meta.periodLabel || emailModalPeriodLabel);
    if (meta.endDate) {
      formData.append("periodEnd", moment(meta.endDate).format("YYYY-MM-DD"));
    }
    const startDate = emailModalInvoices.reduce((min, inv) => {
      const t = moment(inv.date).valueOf();
      return Number.isFinite(t) && (min === 0 || t < min) ? t : min;
    }, 0);
    if (startDate) {
      formData.append("periodStart", moment(startDate).format("YYYY-MM-DD"));
    }

    try {
      const pdfBlob = buildBillingPDFBlob(emailModalInvoices, meta);
      if (pdfBlob) {
        formData.append(
          "files",
          pdfBlob,
          `Invoice-${meta.promocode || "QT"}-${meta.periodLabel || "statement"}.pdf`
        );
      }
    } catch (err) {
      console.error("PDF build failed, sending CSV only:", err);
    }

    this.setState({ emailSending: true });
    const toastId = toast.info("Sending statement…", {
      autoClose: false,
      closeButton: false,
    });

    axios
      .post(`${baseURL}/customer-billing-statement-email`, formData, {
        headers: {
          Authorization: cookie.load("session-id-qt"),
        },
      })
      .then((res) => {
        const sentTo = (res.data?.sentTo || []).join(", ");
        toast.update(toastId, {
          render: res.data?.message || `Statement sent to ${sentTo || accountEmail}`,
          type: "success",
          autoClose: 5000,
          closeButton: true,
          isLoading: false,
        });
        this.setState({
          emailSending: false,
          emailModalOpen: false,
          emailModalInvoices: [],
          emailModalAccountFee: null,
          statementQuota: {
            used: res.data?.used ?? statementQuota.used + 1,
            limit: res.data?.limit ?? statementQuota.limit,
            remaining:
              res.data?.remaining != null
                ? res.data.remaining
                : Math.max(0, (statementQuota.remaining || 1) - 1),
          },
        });
      })
      .catch((err) => {
        const msg =
          err.response?.data?.error ||
          "Could not send statement. Try again or download instead.";
        toast.update(toastId, {
          render: msg,
          type: "error",
          autoClose: 7000,
          closeButton: true,
          isLoading: false,
        });
        if (err.response?.data?.remaining != null) {
          this.setState({
            statementQuota: {
              used: err.response.data.used || statementQuota.used,
              limit: err.response.data.limit || statementQuota.limit,
              remaining: err.response.data.remaining,
            },
          });
        }
        this.setState({ emailSending: false });
      });
  };

  renderBankRow = (label, value, fieldKey) => (
    <div className="qt-bank-row" key={fieldKey || label}>
      <span>{label}</span>
      <strong>
        <span className={fieldKey === "accountNumber" || fieldKey === "branchCode" ? "font-monospace" : ""}>
          {value}
        </span>
        {fieldKey ? (
          <button
            type="button"
            className="qt-copy-btn"
            title="Copy"
            onClick={() => this.copyToClipboard(fieldKey, value)}
          >
            <i
              className={
                "mdi " +
                (this.state.copiedField === fieldKey ? "mdi-check" : "mdi-content-copy")
              }
            />
          </button>
        ) : null}
      </strong>
    </div>
  );

  renderPayPanel = () => {
    if (this.state.canPayBilling === false) return null;
    const { selectedItems, billingProfile } = this.state;
    const outstanding = billingProfile.outstanding_cents || 0;
    const ref = this.getPaymentReference();

    return (
      <div className="qt-pay-panel">
        <div className="qt-pay-card">
          <div className="qt-pay-card-head">
            <h5>
              <i className="mdi mdi-bank" />
              Pay via EFT
            </h5>
          </div>
          <div className="qt-pay-card-body">
            {selectedItems.length > 0 ? (
              <>
                <p className="text-muted small mb-2">
                  {selectedItems.length} trip{selectedItems.length !== 1 ? "s" : ""} selected
                </p>
                {(() => {
                  const trips = this.calculateSelectedTripsTotal();
                  const af = Number(this.state.accountFee) || 0;
                  const afVat = this.getAccountFeeVat(af);
                  const due = trips + af + afVat;
                  return (
                    <div className="qt-pay-breakdown mb-2">
                      <div className="qt-pay-row">
                        <span>Trips</span>
                        <span>{this.formatCurrency(trips)}</span>
                      </div>
                      {af > 0 ? (
                        <>
                          <div className="qt-pay-row">
                            <span>Account fee</span>
                            <span>{this.formatCurrency(af)}</span>
                          </div>
                          <div className="qt-pay-row qt-pay-row--muted">
                            <span>VAT on account fee</span>
                            <span>{this.formatCurrency(afVat)}</span>
                          </div>
                        </>
                      ) : null}
                      <div className="qt-pay-total">
                        <span>Amount due</span>
                        <strong>{this.formatCurrency(due)}</strong>
                      </div>
                    </div>
                  );
                })()}
              </>
            ) : outstanding > 0 ? (
              <div className="qt-pay-total mb-2">
                <span>Outstanding</span>
                <strong>
                  {new Intl.NumberFormat("en-ZA", {
                    style: "currency",
                    currency: "ZAR",
                  }).format(outstanding / 100)}
                </strong>
              </div>
            ) : (
              <p className="text-muted small mb-2">
                Pay a single trip via PayFast from the table, or select multiple trips here for EFT.
              </p>
            )}

            {this.renderBankRow("Account name", BANK_DETAILS.accountName)}
            {this.renderBankRow("Bank", BANK_DETAILS.bank)}
            {this.renderBankRow("Account no.", BANK_DETAILS.accountNumber, "accountNumber")}
            {this.renderBankRow("Branch code", BANK_DETAILS.branchCode, "branchCode")}
            {this.renderBankRow("Type", BANK_DETAILS.accountType)}
            {ref ? this.renderBankRow("Reference", ref, "reference") : null}

            <p className="qt-pay-hint">
              <i className="mdi mdi-information-outline me-1" />
              Use the reference above so we can allocate your payment. Email proof of payment to
              finance@qiktruck.co.za if paying multiple invoices.
            </p>

            {selectedItems.length > 0 ? (
              <div className="d-flex flex-column gap-2 mt-3">
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    color="secondary"
                    outline
                    className="flex-grow-1"
                    onClick={this.downloadSelectedCSV}
                  >
                    <i className="mdi mdi-file-delimited-outline me-1" />
                    CSV
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    className="flex-grow-1"
                    onClick={this.downloadSelectedPDF}
                  >
                    <i className="mdi mdi-file-pdf-box me-1" />
                    PDF
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    className="flex-grow-1"
                    onClick={() =>
                      this.openEmailModal(
                        selectedItems,
                        moment(selectedItems[0].date).format("MMMM YYYY")
                      )
                    }
                  >
                    <i className="mdi mdi-email-outline me-1" />
                    Email
                  </Button>
                </div>
                <Button color="primary" block onClick={this.toggleDrawer}>
                  <i className="mdi mdi-receipt me-1" />
                  Review selection
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <a href="/profile#finance" className="qt-link-card d-block text-decoration-none">
          <h5>
            <i className="mdi mdi-cog-outline me-1" />
            Billing settings
          </h5>
          <p className="mb-0 small text-muted">
            Settlement mode, invoice email, and integration billing - managed in your account profile.
          </p>
        </a>
      </div>
    );
  };

  render() {
    const {
      weeklyDue,
      monthlyDue,
      accountFee,
      isLoading,
      viewMode,
      limit,
      totalCount,
      currentPage,
      hasMoreRecords,
      offset,
      selectedOrder,
      startChat,
      orderFiles,
      orderConversations,
      newComment,
      addingConversation,
      billingProfile,
      showAnalytics,
      statusFilter,
      invoiceSearch,
      summary,
    } = this.state;

    const filteredData = this.getFilteredDisplayData();
    const unpaidOnPage = this.getPageUnpaidTotal();
    const unpaidCount = this.getPageUnpaidCount();
    const outstandingCents = billingProfile.outstanding_cents || 0;
    const selectedCount = this.state.selectedItems.length;

    const totalPages = Math.max(Math.ceil(totalCount / limit), currentPage);
    const startRecord = totalCount > 0 ? offset + 1 : 0;
    const endRecord = Math.min(offset + limit, totalCount);
    const hasPreviousBatch = offset > 0;
    const hasNextBatch = hasMoreRecords;

    const getPageNumbers = () => {
      const pageNumbers = [];
      const maxPagesToShow = 5;
      if (totalPages <= maxPagesToShow) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        let startPage = Math.max(currentPage - Math.floor(maxPagesToShow / 2), 1);
        let endPage = startPage + maxPagesToShow - 1;
        if (endPage > totalPages) {
          endPage = totalPages;
          startPage = Math.max(endPage - maxPagesToShow + 1, 1);
        }
        for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
        if (startPage > 1) {
          pageNumbers.unshift("...");
          pageNumbers.unshift(1);
        }
        if (endPage < totalPages) {
          pageNumbers.push("...");
          pageNumbers.push(totalPages);
        }
      }
      return pageNumbers;
    };

    const chartColors = {
      primary: "rgba(79, 99, 103, 0.75)",
      primaryBorder: "rgba(79, 99, 103, 1)",
      mint: "rgba(52, 209, 191, 0.65)",
      mintBorder: "rgba(52, 209, 191, 1)",
      orange: "rgba(241, 117, 22, 0.65)",
      orangeBorder: "rgba(241, 117, 22, 1)",
    };

    return (
      <React.Fragment>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="qt-billing qt-integrations">
          {this.renderFloatingRowMenu()}
          <Container>
            <div className="qt-page-head">
              <div className="qt-breadcrumb">
                <a href="/dashboard">Dashboard</a>
                <span className="mx-1">/</span>
                Billing
              </div>
              <div className="qt-page-title-row">
                <div>
                  <h1 className="qt-page-title">
                    <i className="mdi mdi-receipt" />
                    Billing &amp; payments
                  </h1>
                  <p className="qt-page-lead mb-0">
                    {billingProfile.partner_billing_applies
                      ? "Trip history and payments for merchant-billed deliveries. Pay individual trips via PayFast on track-order, batch-settle via EFT, or email PDF invoices."
                      : "Trip history and payments for your portal bookings. Settle finished trips via EFT or PayFast, download statements, or email PDF invoices."}
                  </p>
                </div>
                {billingProfile.mode_label ? (
                  <div
                    className="qt-site-chip"
                    title={
                      billingProfile.partner_billing_applies
                        ? "Partner API / widget settlement mode"
                        : "Portal merchant billing - settle finished trips via EFT or PayFast"
                    }
                  >
                    <i
                      className={
                        "mdi " +
                        (billingProfile.partner_billing_applies
                          ? "mdi-credit-card-outline"
                          : "mdi-briefcase-outline")
                      }
                    />
                    {billingProfile.mode_label}
                  </div>
                ) : null}
              </div>
            </div>

            {(outstandingCents > 0 || unpaidCount > 0) && !isLoading ? (
              <div
                className={
                  "qt-alert-banner " +
                  (outstandingCents > 0 ? "qt-alert-banner--danger" : "qt-alert-banner--warning")
                }
              >
                <i className="mdi mdi-alert-circle-outline" />
                <div className="flex-grow-1">
                  <h5>
                    {outstandingCents > 0
                      ? "You have an outstanding balance"
                      : "Unpaid trips on this page"}
                  </h5>
                  <p>
                    {outstandingCents > 0 ? (
                      <>
                        <strong>
                          {new Intl.NumberFormat("en-ZA", {
                            style: "currency",
                            currency: "ZAR",
                          }).format(outstandingCents / 100)}
                        </strong>
                        {billingProfile.outstanding_orders
                          ? ` across ${billingProfile.outstanding_orders} trip${
                              billingProfile.outstanding_orders !== 1 ? "s" : ""
                            }`
                          : ""}
                        . Select trips below and pay via EFT using the banking details.
                      </>
                    ) : (
                      <>
                        {unpaidCount} unpaid trip{unpaidCount !== 1 ? "s" : ""} totalling{" "}
                        <strong>{this.formatCurrency(unpaidOnPage)}</strong> on this page. Select them
                        to get a payment reference.
                      </>
                    )}
                  </p>
                </div>
                {selectedCount === 0 && unpaidCount > 0 ? (
                  <Button
                    color="warning"
                    size="sm"
                    outline
                    onClick={() => {
                      const unpaid = this.state.invoices.filter((i) => !this.isPaid(i));
                      this.setState({ selectedItems: unpaid, isDrawerOpen: true });
                    }}
                  >
                    Select all unpaid
                  </Button>
                ) : null}
              </div>
            ) : null}

            <div className="qt-kpi-grid">
              <div className="qt-kpi">
                <div className="qt-kpi-icon qt-kpi-icon--orange">
                  <i className="mdi mdi-calendar-week" />
                </div>
                <div>
                  <p className="qt-kpi-label">This week</p>
                  <p className="qt-kpi-value">{this.formatCurrency(weeklyDue)}</p>
                  <p className="qt-kpi-hint">Finished trips, last 7 days</p>
                </div>
              </div>
              <div className="qt-kpi">
                <div className="qt-kpi-icon qt-kpi-icon--teal">
                  <i className="mdi mdi-calendar-month" />
                </div>
                <div>
                  <p className="qt-kpi-label">This month</p>
                  <p className="qt-kpi-value">{this.formatCurrency(monthlyDue)}</p>
                  <p className="qt-kpi-hint">
                    {accountFee > 0
                      ? "Trips + account fee incl. VAT"
                      : "Finished trips, calendar month"}
                  </p>
                </div>
              </div>
              <div className="qt-kpi">
                <div className="qt-kpi-icon qt-kpi-icon--mint">
                  <i className="mdi mdi-tag-outline" />
                </div>
                <div>
                  <p className="qt-kpi-label">Account fee</p>
                  <p className="qt-kpi-value">{this.formatCurrency(accountFee)}</p>
                  <p className="qt-kpi-hint">
                    {accountFee > 0
                      ? `+ ${this.formatCurrency(this.getAccountFeeVat())} VAT on invoice`
                      : "No fee on this profile"}
                  </p>
                </div>
              </div>
              <div className="qt-kpi">
                <div className="qt-kpi-icon qt-kpi-icon--teal">
                  <i className="mdi mdi-truck-delivery" />
                </div>
                <div>
                  <p className="qt-kpi-label">All-time trips</p>
                  <p className="qt-kpi-value">{summary.orders || 0}</p>
                  <p className="qt-kpi-hint">
                    {summary.tripsperday
                      ? `~${summary.tripsperday} per day recently`
                      : "Total orders on account"}
                  </p>
                </div>
              </div>
            </div>

            <Row>
              <Col lg={8}>
                <div className="qt-card">
                  <div className="qt-card-body">
                    <div className="qt-section-head">
                      <div className="qt-section-icon qt-section-icon--teal">
                        <i className="mdi mdi-history" />
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="qt-section-title">Trip history</h4>
                        <p className="qt-section-desc mb-0">
                          Open track-order to pay a trip via PayFast, or select multiple trips for EFT.
                        </p>
                      </div>
                    </div>

                    <div className="qt-toolbar">
                      <div className="qt-toolbar-left">
                        <div className="qt-segment">
                          <button
                            type="button"
                            className={
                              "qt-segment-btn" + (viewMode === "monthly" ? " is-active" : "")
                            }
                            onClick={() => this.toggleView("monthly")}
                          >
                            Monthly
                          </button>
                          <button
                            type="button"
                            className={
                              "qt-segment-btn" + (viewMode === "weekly" ? " is-active" : "")
                            }
                            onClick={() => this.toggleView("weekly")}
                          >
                            Weekly
                          </button>
                        </div>
                        <div className="qt-segment">
                          {[
                            { id: "all", label: "All" },
                            { id: "unpaid", label: "Unpaid" },
                            { id: "paid", label: "Paid" },
                          ].map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              className={
                                "qt-segment-btn" + (statusFilter === f.id ? " is-active" : "")
                              }
                              onClick={() => this.setState({ statusFilter: f.id })}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="qt-toolbar-right">
                        <div className="qt-date-filter d-flex align-items-center flex-wrap gap-2">
                          <Input
                            type="date"
                            bsSize="sm"
                            aria-label="From date"
                            value={this.state.dateFrom}
                            onChange={(e) =>
                              this.handleDateFilterChange("dateFrom", e.target.value)
                            }
                          />
                          <span className="text-muted small">to</span>
                          <Input
                            type="date"
                            bsSize="sm"
                            aria-label="To date"
                            value={this.state.dateTo}
                            onChange={(e) =>
                              this.handleDateFilterChange("dateTo", e.target.value)
                            }
                          />
                          <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={this.applyDateFilter}
                          >
                            Apply
                          </Button>
                          {this.state.dateFrom || this.state.dateTo ? (
                            <Button
                              color="link"
                              size="sm"
                              className="p-0 text-decoration-none"
                              onClick={this.clearDateFilter}
                            >
                              Clear dates
                            </Button>
                          ) : null}
                        </div>
                        <div className="qt-search">
                          <i className="mdi mdi-magnify" />
                          <Input
                            type="search"
                            placeholder="Search invoice #"
                            value={invoiceSearch}
                            onChange={(e) => this.setState({ invoiceSearch: e.target.value })}
                            bsSize="sm"
                          />
                        </div>
                      </div>
                    </div>

                    {isLoading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="text-muted small mt-2 mb-0">Loading trip history…</p>
                      </div>
                    ) : filteredData.length === 0 ? (
                      <div className="qt-empty">
                        <i className="mdi mdi-receipt" />
                        <h5>No trips match your filters</h5>
                        <p className="mb-0">
                          {statusFilter !== "all" || invoiceSearch
                            ? "Try clearing filters or switching between monthly and weekly view."
                            : "Completed merchant-billed trips will appear here."}
                        </p>
                      </div>
                    ) : (
                      <>
                        {filteredData.map((period, index) => (
                          <div key={index} className="qt-period-block">
                            <div className="qt-period-head">
                              <div>
                                <h5 className="qt-period-title mb-0">{period.period}</h5>
                                {viewMode === "monthly" && period.accountFee > 0 ? (
                                  <p className="qt-period-fee-hint mb-0">
                                    Trips {this.formatCurrency(period.tripsTotal)}
                                    {" + "}
                                    fee {this.formatCurrency(period.accountFee)}
                                    {" + "}
                                    VAT {this.formatCurrency(period.accountFeeVat)}
                                  </p>
                                ) : null}
                              </div>
                              <div className="qt-period-actions">
                                <button
                                  type="button"
                                  className="qt-period-dl-btn"
                                  title={`Download CSV for ${period.period}`}
                                  onClick={() => this.downloadPeriodCSV(period)}
                                >
                                  <i className="mdi mdi-file-delimited-outline" />
                                  CSV
                                </button>
                                <button
                                  type="button"
                                  className="qt-period-dl-btn"
                                  title={`Download PDF invoice for ${period.period}`}
                                  onClick={() => this.downloadPeriodPDF(period)}
                                >
                                  <i className="mdi mdi-file-pdf-box" />
                                  PDF
                                </button>
                                <button
                                  type="button"
                                  className="qt-period-dl-btn"
                                  title={`Email statement for ${period.period}`}
                                  onClick={() =>
                                    this.openEmailModal(
                                      period.invoices,
                                      period.period,
                                      this.state.viewMode === "monthly"
                                        ? period.accountFee != null
                                          ? period.accountFee
                                          : this.state.accountFee
                                        : 0
                                    )
                                  }
                                >
                                  <i className="mdi mdi-email-outline" />
                                  Email
                                </button>
                                <span className="qt-period-total">
                                  {this.formatCurrency(period.totalAmount)}
                                </span>
                              </div>
                            </div>
                            <div className="qt-invoice-table-wrap">
                              <Table className="qt-invoice-table mb-0" hover>
                                <thead>
                                  <tr>
                                    {this.state.canPayBilling !== false ? (
                                    <th style={{ width: 40 }}>
                                      <Input
                                        type="checkbox"
                                        aria-label={`Select all in ${period.period}`}
                                        checked={
                                          period.invoices.length > 0 &&
                                          period.invoices.every((i) =>
                                            this.state.selectedItems.some((s) => s.id === i.id)
                                          )
                                        }
                                        onChange={() => this.togglePeriodSelectAll(period)}
                                      />
                                    </th>
                                    ) : null}
                                    <th>Trip</th>
                                    <th>Date</th>
                                    <th className="d-none d-md-table-cell">Promo</th>
                                    <th>Amount</th>
                                    <th>Status</th>
                                    <th className="qt-col-menu" />
                                  </tr>
                                </thead>
                                <tbody>
                                  {period.invoices.map((invoice, idx) => {
                                    const isSelected = this.state.selectedItems.some(
                                      (item) => item.id === invoice.id
                                    );
                                    const paid = this.isPaid(invoice);
                                    const trackUrl = this.getTrackOrderUrl(invoice);
                                    const payUrl = paid ? null : this.getTrackOrderUrl(invoice, { payNow: true });
                                    return (
                                      <tr
                                        key={idx}
                                        className={isSelected ? "is-selected" : ""}
                                      >
                                        {this.state.canPayBilling !== false ? (
                                        <td>
                                          <Input
                                            type="checkbox"
                                            aria-label={`Select invoice ${invoice.id}`}
                                            checked={isSelected}
                                            onChange={() => this.toggleItemSelection(invoice)}
                                          />
                                        </td>
                                        ) : null}
                                        <td>
                                          <button
                                            type="button"
                                            className="qt-invoice-id btn btn-link p-0 border-0 text-start"
                                            title="View trip"
                                            onClick={(e) => this.openOrderDetails(invoice, e)}
                                          >
                                            #{invoice.id}
                                          </button>
                                        </td>
                                        <td>{moment(invoice.date).format("DD MMM YY")}</td>
                                        <td className="d-none d-md-table-cell text-muted">
                                          {invoice.promocode || "-"}
                                        </td>
                                        <td className="qt-col-amount">
                                          {this.formatCurrency(this.getInvoiceAmount(invoice))}
                                        </td>
                                        <td>
                                          <span
                                            className={
                                              "qt-status-dot " +
                                              (paid ? "qt-status-dot--paid" : "qt-status-dot--unpaid")
                                            }
                                            title={paid ? "Paid" : "Unpaid"}
                                          >
                                            {paid ? "Paid" : "Due"}
                                          </span>
                                        </td>
                                        <td className="qt-col-menu">
                                          {this.renderInvoiceRowMenu(invoice, {
                                            trackUrl,
                                            payUrl,
                                            paid,
                                          })}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        ))}

                        <div className="qt-pagination-wrap">
                          <Button
                            color="link"
                            className="p-0 text-decoration-none"
                            disabled={!hasPreviousBatch}
                            onClick={this.handlePreviousBatch}
                          >
                            <i className="mdi mdi-chevron-left" /> Previous {limit}
                          </Button>

                          <span className="qt-pagination-info">
                            Showing {startRecord}-{endRecord} of {totalCount}
                          </span>

                          <Button
                            color="link"
                            className="p-0 text-decoration-none"
                            disabled={!hasNextBatch}
                            onClick={this.handleNextBatch}
                          >
                            Next {limit} <i className="mdi mdi-chevron-right" />
                          </Button>
                        </div>

                        {totalPages > 1 ? (
                          <div className="d-flex justify-content-center mt-3">
                            <Pagination size="sm" aria-label="Billing history pages">
                              <PaginationItem disabled={currentPage <= 1}>
                                <PaginationLink
                                  previous
                                  onClick={(e) => {
                                    e.preventDefault();
                                    this.handlePageChange(currentPage - 1);
                                  }}
                                  href="#"
                                />
                              </PaginationItem>
                              {getPageNumbers().map((page, index) =>
                                page === "..." ? (
                                  <PaginationItem key={`ellipsis-${index}`} disabled>
                                    <PaginationLink onClick={(e) => e.preventDefault()} href="#">
                                      …
                                    </PaginationLink>
                                  </PaginationItem>
                                ) : (
                                  <PaginationItem key={`page-${page}`} active={page === currentPage}>
                                    <PaginationLink
                                      onClick={(e) => {
                                        e.preventDefault();
                                        this.handlePageChange(page);
                                      }}
                                      href="#"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                )
                              )}
                              <PaginationItem disabled={currentPage >= totalPages}>
                                <PaginationLink
                                  next
                                  onClick={(e) => {
                                    e.preventDefault();
                                    this.handlePageChange(currentPage + 1);
                                  }}
                                  href="#"
                                />
                              </PaginationItem>
                            </Pagination>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className="qt-analytics-toggle"
                  onClick={() => this.setState({ showAnalytics: !showAnalytics })}
                >
                  <i className={"mdi " + (showAnalytics ? "mdi-chevron-up" : "mdi-chart-bar")} />
                  {showAnalytics ? "Hide analytics" : "Show spend analytics"}
                  {!showAnalytics && this.getDateRange() ? (
                    <span className="text-muted fw-normal">· {this.getDateRange()}</span>
                  ) : null}
                </button>

                {showAnalytics ? (
                  <div className="qt-section-panel">
                    <Row>
                      <Col lg={7}>
                        <div className="qt-chart-card">
                          <h6>
                            {viewMode === "monthly" ? "Monthly" : "Weekly"} billing trends
                          </h6>
                          <div className="qt-chart-wrap">
                            {!isLoading ? (
                              <Bar
                                data={{
                                  ...(viewMode === "monthly"
                                    ? this.getMonthlyChartData()
                                    : this.getWeeklyChartData()),
                                  datasets: (viewMode === "monthly"
                                    ? this.getMonthlyChartData()
                                    : this.getWeeklyChartData()
                                  ).datasets.map((d) => ({
                                    ...d,
                                    backgroundColor: chartColors.primary,
                                    borderColor: chartColors.primaryBorder,
                                  })),
                                }}
                                options={{
                                  ...this.getChartOptions(""),
                                  plugins: { ...this.getChartOptions("").plugins, title: { display: false } },
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      </Col>
                      <Col lg={5}>
                        <div className="qt-chart-card">
                          <h6>Paid vs unpaid (this page)</h6>
                          <div className="qt-chart-wrap">
                            {!isLoading ? (
                              <Doughnut
                                data={{
                                  ...this.getPaidVsUnpaidData(),
                                  datasets: this.getPaidVsUnpaidData().datasets.map((d) => ({
                                    ...d,
                                    backgroundColor: [chartColors.mint, chartColors.orange],
                                    borderColor: [chartColors.mintBorder, chartColors.orangeBorder],
                                  })),
                                }}
                                options={{
                                  ...this.getChartOptions(""),
                                  plugins: { ...this.getChartOptions("").plugins, title: { display: false } },
                                }}
                              />
                            ) : null}
                          </div>
                        </div>
                      </Col>
                      <Col xs={12}>
                        <div className="qt-chart-card">
                          <h6>Payment status over time</h6>
                          <div className="qt-chart-wrap">
                            {!isLoading ? (
                              <Bar
                                data={this.getPaymentStatusTimelineData()}
                                options={this.getStackedChartOptions("")}
                              />
                            ) : null}
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                ) : null}
              </Col>

              <Col lg={4}>{this.renderPayPanel()}</Col>
            </Row>
          </Container>
        </div>

        {selectedCount > 0 ? (
          <div className="qt-floating-bar">
            <div className="qt-floating-bar-info">
              <i className="mdi mdi-checkbox-marked-circle-outline text-primary" style={{ fontSize: "1.4rem" }} />
              <span>
                <strong>{selectedCount}</strong> trip{selectedCount !== 1 ? "s" : ""} selected ·{" "}
                <strong>{this.formatCurrency(this.calculateSelectedTotal())}</strong>
              </span>
            </div>
            <div className="d-flex gap-2">
              <Button color="link" className="text-danger" onClick={this.clearSelection}>
                Clear
              </Button>
              <Button color="primary" onClick={this.toggleDrawer}>
                Pay via EFT
              </Button>
            </div>
          </div>
        ) : null}

        {/* Order Details Component */}
        <OrderDetails
          order={selectedOrder}
          startChat={startChat}
          setStartChat={(value) => this.setState({ startChat: value })}
          setOrder={(order) => this.setState({ selectedOrder: order })}
          orderFiles={orderFiles}
          orderconversations={orderConversations}
          getOrderFiles={this.getOrderFiles}
          getOrderConversations={this.getOrderConversations}
          newComment={newComment}
          setNewComment={(value) => this.setState({ newComment: value })}
          addingConversation={addingConversation}
          addConversation={this.addConversation}
          getImage={this.getImage}
          onDownloadInvoice={this.downloadInvoice}
          payUrl={
            selectedOrder && !this.isPaid(selectedOrder)
              ? getTrackOrderPayUrl(
                  selectedOrder,
                  this.getInvoiceEmail(selectedOrder)
                )
              : null
          }
          orderPaid={selectedOrder ? this.isPaid(selectedOrder) : false}
          downloadingInvoice={
            selectedOrder &&
            String(this.state.downloadingInvoiceId) === String(selectedOrder.orderid)
          }
        />

        {/* Selected trips / EFT payment drawer */}
        <Offcanvas
          isOpen={this.state.isDrawerOpen}
          toggle={this.toggleDrawer}
          direction="end"
          className="qt-billing-drawer"
        >
          <OffcanvasHeader toggle={this.toggleDrawer} className="qt-drawer-header">
            <div className="qt-drawer-header-inner">
              <span className="qt-drawer-header-icon">
                <i className="mdi mdi-clipboard-check" />
              </span>
              <div>
                <strong className="qt-drawer-title">Pay selected trips</strong>
                <p className="qt-drawer-subtitle mb-0">
                  {this.state.selectedItems.length} trip
                  {this.state.selectedItems.length !== 1 ? "s" : ""} · EFT settlement
                </p>
              </div>
            </div>
          </OffcanvasHeader>
          <OffcanvasBody className="qt-drawer-body">
            {this.state.selectedItems.length === 0 ? (
              <div className="qt-drawer-empty">
                <i className="mdi mdi-checkbox-marked-circle-outline" />
                <h5>No trips selected</h5>
                <p className="mb-0">
                  Select unpaid trips from the table to build an EFT payment.
                </p>
              </div>
            ) : (
              <>
                <div className="qt-drawer-section">
                  <div className="qt-drawer-section-head">
                    <h6 className="mb-0">Selected trips</h6>
                    <button
                      type="button"
                      className="qt-drawer-clear-btn"
                      onClick={this.clearSelection}
                    >
                      Clear all
                    </button>
                  </div>

                  <ul className="qt-drawer-trip-list">
                    {this.state.selectedItems.map((item) => (
                      <li key={item.id} className="qt-drawer-trip">
                        <div className="qt-drawer-trip-main">
                          <div className="qt-drawer-trip-id">
                            <span className="qt-drawer-trip-hash">#{item.id}</span>
                            <span
                              className={
                                "qt-drawer-status " +
                                (this.isPaid(item) ? "is-paid" : "is-unpaid")
                              }
                            >
                              {this.isPaid(item) ? "Paid" : "Unpaid"}
                            </span>
                          </div>
                          <div className="qt-drawer-trip-meta">
                            {moment(item.date).format("DD MMM YYYY · HH:mm")}
                            {item.promocode ? (
                              <span className="qt-drawer-promo">{item.promocode}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="qt-drawer-trip-aside">
                          <strong>{this.formatCurrency(this.getInvoiceAmount(item))}</strong>
                          <button
                            type="button"
                            className="qt-drawer-remove"
                            title="Remove from selection"
                            aria-label={`Remove invoice ${item.id}`}
                            onClick={() => this.removeSelectedItem(item.id)}
                          >
                            <i className="mdi mdi-close" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {(() => {
                  const { accountFee } = this.state;
                  const af = Number(accountFee) || 0;
                  const afVat = this.getAccountFeeVat(af);
                  const totals = this.state.selectedItems.reduce(
                    (acc, item) => {
                      const b = this.getInvoiceBreakdown(item);
                      acc.gross += b.gross;
                      acc.commission += b.commission;
                      acc.vat += b.vat;
                      acc.driverpay += b.driverpay;
                      return acc;
                    },
                    { gross: 0, commission: 0, vat: 0, driverpay: 0 }
                  );
                  const invoiceTotal = totals.gross + af + afVat;
                  return (
                    <div className="qt-drawer-breakdown">
                      <div className="qt-drawer-section-head">
                        <h6 className="mb-0">Invoice breakdown</h6>
                      </div>
                      <div className="qt-drawer-row">
                        <span>Trips subtotal</span>
                        <strong>{this.formatCurrency(totals.gross)}</strong>
                      </div>
                      <div className="qt-drawer-row qt-drawer-row--muted">
                        <span>↳ Service fee (excl. VAT)</span>
                        <span>{this.formatCurrency(totals.commission)}</span>
                      </div>
                      <div className="qt-drawer-row qt-drawer-row--muted">
                        <span>↳ VAT on service fee (15%)</span>
                        <span>{this.formatCurrency(totals.vat)}</span>
                      </div>
                      <div className="qt-drawer-row qt-drawer-row--muted">
                        <span>↳ Driver pay</span>
                        <span>{this.formatCurrency(totals.driverpay)}</span>
                      </div>
                      {af > 0 ? (
                        <>
                          <div className="qt-drawer-row">
                            <span>Account fee (SaaS)</span>
                            <strong>{this.formatCurrency(af)}</strong>
                          </div>
                          <div className="qt-drawer-row qt-drawer-row--muted">
                            <span>↳ VAT on account fee (15%)</span>
                            <span>{this.formatCurrency(afVat)}</span>
                          </div>
                        </>
                      ) : null}
                      <div className="qt-drawer-row qt-drawer-row--total">
                        <span>
                          {af > 0 ? "Invoice amount" : "Amount due"}
                        </span>
                        <strong>{this.formatCurrency(invoiceTotal)}</strong>
                      </div>
                      {af > 0 ? (
                        <p className="qt-drawer-fee-note mb-0">
                          Invoice amount includes the monthly account fee + VAT
                          ({this.formatCurrency(af + afVat)}).
                        </p>
                      ) : null}
                    </div>
                  );
                })()}

                <div className="qt-drawer-bank">
                  <div className="qt-drawer-bank-head">
                    <i className="mdi mdi-bank me-2" />
                    Pay via EFT · QikTruck FNB
                  </div>
                  <div className="qt-drawer-bank-body">
                    {this.renderBankRow("Account name", BANK_DETAILS.accountName)}
                    {this.renderBankRow("Bank", BANK_DETAILS.bank)}
                    {this.renderBankRow(
                      "Account no.",
                      BANK_DETAILS.accountNumber,
                      "accountNumber"
                    )}
                    {this.renderBankRow(
                      "Branch code",
                      BANK_DETAILS.branchCode,
                      "branchCode"
                    )}
                    {this.renderBankRow("Type", BANK_DETAILS.accountType)}
                    {this.getPaymentReference()
                      ? this.renderBankRow(
                          "Reference",
                          this.getPaymentReference(),
                          "reference"
                        )
                      : null}
                    <p className="qt-pay-hint mt-2 mb-0">
                      <i className="mdi mdi-information-outline me-1" />
                      Use the reference above when paying. For multiple invoices, email proof of
                      payment listing all invoice IDs to finance@qiktruck.co.za.
                    </p>
                  </div>
                </div>
              </>
            )}
          </OffcanvasBody>
          {this.state.selectedItems.length > 0 ? (
            <div className="qt-drawer-footer">
              <div>
                <span className="qt-drawer-footer-label">Amount due</span>
                <strong className="qt-drawer-footer-amount">
                  {this.formatCurrency(this.calculateSelectedTotal())}
                </strong>
              </div>
              <div className="qt-drawer-footer-actions">
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={this.downloadSelectedCSV}
                  title="Download CSV statement"
                >
                  <i className="mdi mdi-file-delimited-outline me-1" />
                  CSV
                </Button>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={this.downloadSelectedPDF}
                  title="Download PDF tax invoice"
                >
                  <i className="mdi mdi-file-pdf-box me-1" />
                  PDF
                </Button>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={() =>
                    this.openEmailModal(
                      this.state.selectedItems,
                      moment(this.state.selectedItems[0].date).format("MMMM YYYY")
                    )
                  }
                  title="Email statement to yourself and up to 2 others"
                >
                  <i className="mdi mdi-email-outline me-1" />
                  Email
                </Button>
                <Button color="secondary" outline onClick={this.toggleDrawer}>
                  Done
                </Button>
              </div>
            </div>
          ) : null}
        </Offcanvas>

        <Modal
          isOpen={this.state.emailModalOpen}
          toggle={this.closeEmailModal}
          centered
          className="qt-statement-email-modal"
        >
          <ModalHeader toggle={this.closeEmailModal}>
            Email billing statement
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small mb-3">
              Sends a PDF + CSV for{" "}
              <strong>
                {this.state.emailModalInvoices.length} trip
                {this.state.emailModalInvoices.length !== 1 ? "s" : ""}
              </strong>
              {this.state.emailModalPeriodLabel
                ? ` (${this.state.emailModalPeriodLabel})`
                : ""}
              . Always includes your account email; you can add up to 2 more.
            </p>

            <div className="mb-3">
              <Label className="form-label small fw-semibold mb-1">
                Your email (always included)
              </Label>
              <Input
                type="email"
                value={this.state.accountEmail || ""}
                disabled
                readOnly
              />
            </div>

            <div className="mb-3">
              <Label className="form-label small fw-semibold mb-1">
                Additional recipient 1 (optional)
              </Label>
              <Input
                type="email"
                placeholder="finance@company.co.za"
                value={this.state.emailExtra1}
                disabled={this.state.emailSending}
                onChange={(e) => this.setState({ emailExtra1: e.target.value })}
              />
            </div>

            <div className="mb-3">
              <Label className="form-label small fw-semibold mb-1">
                Additional recipient 2 (optional)
              </Label>
              <Input
                type="email"
                placeholder="accounts@company.co.za"
                value={this.state.emailExtra2}
                disabled={this.state.emailSending}
                onChange={(e) => this.setState({ emailExtra2: e.target.value })}
              />
            </div>

            <p
              className={
                "qt-email-quota mb-0 small " +
                ((this.state.statementQuota.remaining || 0) <= 0
                  ? "text-danger"
                  : "text-muted")
              }
            >
              <i className="mdi mdi-clock-outline me-1" />
              {(this.state.statementQuota.remaining || 0) <= 0
                ? `Daily limit reached (${this.state.statementQuota.limit}/day). Try again tomorrow.`
                : `${this.state.statementQuota.remaining} of ${this.state.statementQuota.limit} statement emails left today.`}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              onClick={this.closeEmailModal}
              disabled={this.state.emailSending}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={this.sendStatementEmail}
              disabled={
                this.state.emailSending ||
                (this.state.statementQuota.remaining || 0) <= 0 ||
                !this.state.accountEmail
              }
            >
              {this.state.emailSending ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Sending…
                </>
              ) : (
                <>
                  <i className="mdi mdi-send me-1" />
                  Send statement
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

export default Billing;
